import { useNavigate } from '@tanstack/react-router';
import { BrainCircuit, ExternalLink, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';

import { CONNECT_CARD_SCHEMA } from '../types';
import type {
  InteractionProposal,
  PortfolioPlanRow,
  ProposalDecision,
  ProposalDecisionKind,
} from '../types';

import { ConnectCard } from './ConnectCard';

interface ProposalCardProps {
  proposal: InteractionProposal;
  onDecide: (decision: ProposalDecision) => void;
}

export function ProposalCard(props: ProposalCardProps) {
  // A Connect card is never approved or rejected here: it answers from its own record in the
  // message's `connectCards` (CC D15), so one that reaches this renderer shows as a record.
  if (props.proposal.schema === CONNECT_CARD_SCHEMA) {
    return (
      <ConnectCard card={{ proposal: props.proposal, modelId: '', arrivedAt: 0, record: true }} />
    );
  }
  return <DecisionCard {...props} />;
}

function DecisionCard({ proposal, onDecide }: ProposalCardProps) {
  const { t } = useTranslation('ai-cio');
  const navigate = useNavigate();
  const isMemory = proposal.schema === 'remember_preference_v1';
  const isHandoff = proposal.schema === 'crm_handoff_v1';
  const handoff = isHandoff ? readHandoff(proposal.content) : null;

  const decide = (decision: ProposalDecisionKind) => {
    onDecide({ proposal, decision });
    // The hand-off's "approval" is a navigation. Nothing was written by the
    // assistant and nothing will be until the user submits the CRM's own form,
    // so the decision above only resolves the interaction — this is the part
    // that actually does something.
    if (decision === 'approve' && handoff?.path) {
      void navigate({ to: handoff.path, search: handoff.params });
    }
  };

  const heading = isMemory
    ? t('proposal.memoryTitle', 'Add to AI Memory')
    : isHandoff
      ? proposal.title || t('proposal.handoffTitle', 'Open in the CRM')
      : proposal.title || t('proposal.title', 'Approval required');

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        {isMemory ? (
          <BrainCircuit className="size-4 shrink-0 text-primary" aria-hidden />
        ) : isHandoff ? (
          <ExternalLink className="size-4 shrink-0 text-primary" aria-hidden />
        ) : (
          <ShieldCheck className="size-4 shrink-0 text-primary" aria-hidden />
        )}
        <p className="text-sm font-medium text-foreground">{heading}</p>
      </div>

      <div className="p-4">
        <ProposalBody proposal={proposal} />

        {proposal.rationale && (
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{proposal.rationale}</p>
        )}

        {proposal.tradeoffs.length > 0 && (
          <div className="mt-3 rounded-md border border-warning/30 bg-warning/10 p-3">
            <div className="mb-1 flex items-center gap-1.5">
              <TriangleAlert className="size-3.5 text-warning" aria-hidden />
              <p className="text-xs font-medium text-warning">
                {t('proposal.tradeoffs', 'Worth knowing')}
              </p>
            </div>
            <ul className="list-disc space-y-0.5 ps-4 text-xs text-muted-foreground">
              {proposal.tradeoffs.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              decide('approve');
            }}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            {isMemory
              ? t('proposal.save', 'Save to AI Memory')
              : isHandoff
                ? t('proposal.openInCrm', 'Open in CRM')
                : t('proposal.approve', 'Approve')}
          </button>
          <button
            type="button"
            onClick={() => {
              decide('restart');
            }}
            className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
          >
            {isMemory
              ? t('proposal.dismiss', "Don't save")
              : isHandoff
                ? t('proposal.notNow', 'Not now')
                : t('proposal.reject', 'Reject')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProposalBody({ proposal }: { proposal: InteractionProposal }) {
  switch (proposal.schema) {
    case 'remember_preference_v1':
      return <RememberPreferenceBody content={proposal.content} />;
    case 'portfolio_plan_v1':
      return <PortfolioPlanBody content={proposal.content} />;
    case 'crm_handoff_v1':
      return <CrmHandoffBody content={proposal.content} />;
    default:
      return (
        <p className="whitespace-pre-wrap text-sm text-foreground">
          {proposal.plainText || proposal.title}
        </p>
      );
  }
}

function RememberPreferenceBody({ content }: { content: Record<string, unknown> }) {
  const { t } = useTranslation('ai-cio');
  const summary = typeof content.summary === 'string' ? content.summary : '';
  return (
    <>
      <p className="text-sm text-muted-foreground">
        {t(
          'proposal.memoryPrompt',
          'Should I save this interaction to your profile? This helps me remember your preferences and provide more accurate responses in the future.',
        )}
      </p>
      {summary && (
        <p className="mt-3 rounded-md border bg-muted/50 px-3 py-2 text-sm text-foreground">
          {summary}
        </p>
      )}
    </>
  );
}

function PortfolioPlanBody({ content }: { content: Record<string, unknown> }) {
  const { t } = useTranslation('ai-cio');
  const rows = toRows(content.allocation);
  const notes = Array.isArray(content.notes)
    ? content.notes.filter((n): n is string => typeof n === 'string')
    : [];
  const goal = typeof content.goal_summary === 'string' ? content.goal_summary : '';

  return (
    <>
      {goal && <p className="mb-3 text-sm text-muted-foreground">{goal}</p>}

      <dl className="grid grid-cols-3 gap-3">
        <Stat label={t('proposal.capital', 'Capital')} value={formatMoney(content.capital)} />
        <Stat
          label={t('proposal.riskLevel', 'Risk level')}
          value={formatRisk(content.risk_level)}
        />
        <Stat
          label={t('proposal.horizon', 'Horizon')}
          value={formatHorizon(content.horizon_years, t)}
        />
      </dl>

      {rows.length > 0 && (
        <ul className="mt-3 divide-y rounded-md border">
          {rows.map((row, i) => (
            <li key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="min-w-0 truncate text-foreground">
                {row.asset_class}
                {refinement(row) && (
                  <span className="text-muted-foreground"> ({refinement(row)})</span>
                )}
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatShare(row.share)} · {formatMoney(row.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {notes.length > 0 && (
        <ul className="mt-3 list-disc space-y-0.5 ps-4 text-xs text-muted-foreground">
          {notes.map((note, i) => (
            <li key={i}>{note}</li>
          ))}
        </ul>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/40 px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn('mt-0.5 text-sm font-medium text-foreground', 'tabular-nums')}>{value}</dd>
    </div>
  );
}

function toRows(raw: unknown): PortfolioPlanRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
    .map((r) => ({
      asset_class: typeof r.asset_class === 'string' ? r.asset_class : '—',
      share: toNumber(r.share) ?? NaN,
      amount: toNumber(r.amount) ?? NaN,
      country_id: typeof r.country_id === 'string' ? r.country_id : null,
      industry_id: typeof r.industry_id === 'string' ? r.industry_id : null,
    }));
}

function refinement(row: PortfolioPlanRow): string {
  return [row.country_id, row.industry_id].filter(Boolean).join(' / ');
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatMoney(value: unknown): string {
  const n = toNumber(value);
  if (n === null) return '—';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatShare(value: unknown): string {
  const n = toNumber(value);
  if (n === null) return '—';
  return `${(n * 100).toFixed(0)}%`;
}

function formatRisk(value: unknown): string {
  const n = toNumber(value);
  if (n === null) return '—';
  return n.toFixed(2);
}

function formatHorizon(value: unknown, t: (key: string, fallback: string) => string): string {
  const n = toNumber(value);
  if (n === null || n <= 0) return '—';
  return `${n.toString()} ${t('proposal.years', 'yrs')}`;
}

/**
 * The hand-off card's payload, read defensively.
 *
 * **The path is re-checked here even though the server pins it.** The route
 * comes from a table in the MCP registry and the model names a key, never a
 * path — but this is the component that performs the navigation, and a check at
 * the point of use costs nothing. `//evil.test` is the one that matters: it
 * starts with a slash, passes a naive prefix test, and a browser reads it as a
 * host. An unusable path yields no button rather than a guess.
 */
function readHandoff(content: Record<string, unknown>): {
  path: string;
  params: Record<string, string | string[]>;
} | null {
  const path = typeof content.path === 'string' ? content.path : '';
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('://')) {
    return null;
  }
  const raw =
    typeof content.params === 'object' && content.params !== null
      ? (content.params as Record<string, unknown>)
      : {};
  const params: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(raw)) {
    // Arrays are kept as arrays. Every filter field in this app's search
    // schemas is `z.array(z.string()).optional().catch(undefined)`, and that
    // `.catch` SWALLOWS a scalar rather than refusing it — so flattening one
    // here would open the page with the filter silently unapplied, which looks
    // exactly like it worked. The orchestrator decides the shape from the
    // registry; this only rejects values that are neither.
    if (typeof value === 'string' && value !== '') {
      params[key] = value;
    } else if (Array.isArray(value)) {
      const items = value.filter((v): v is string => typeof v === 'string' && v !== '');
      if (items.length > 0) params[key] = items;
    }
  }
  return { path, params };
}

function CrmHandoffBody({ content }: { content: Record<string, unknown> }) {
  const { t } = useTranslation('ai-cio');
  const label = typeof content.destination_label === 'string' ? content.destination_label : '';
  const note = typeof content.note === 'string' ? content.note : '';
  const fields = Array.isArray(content.fields)
    ? (content.fields as unknown[]).filter(
        (f): f is { label: string; value: string } =>
          typeof f === 'object' &&
          f !== null &&
          typeof (f as { label?: unknown }).label === 'string' &&
          typeof (f as { value?: unknown }).value === 'string',
      )
    : [];
  const dropped = Array.isArray(content.dropped_keys)
    ? (content.dropped_keys as unknown[]).filter((k): k is string => typeof k === 'string')
    : [];
  const target = readHandoff(content);

  return (
    <>
      <p className="text-sm text-muted-foreground">
        {target
          ? t('proposal.handoffTarget', 'Opens {{label}} with these details already filled in.', {
              label: label || t('proposal.handoffFallbackLabel', 'the page'),
            })
          : t(
              'proposal.handoffUnknown',
              "I couldn't work out which page this is — please open it from the menu.",
            )}
      </p>

      {fields.length > 0 ? (
        <dl className="mt-3 space-y-1 rounded-md border bg-muted/50 px-3 py-2">
          {fields.map((row) => (
            <div key={row.label} className="flex flex-wrap gap-x-2 text-sm">
              <dt className="text-muted-foreground">{row.label}:</dt>
              <dd className="font-medium text-foreground">{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          {t('proposal.handoffEmpty', 'Nothing to fill in yet — the form opens empty.')}
        </p>
      )}

      {note && <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{note}</p>}

      {dropped.length > 0 && (
        // Surfaced rather than swallowed: a value with no field on this page is
        // one the user will have to enter themselves.
        <p className="mt-3 text-xs leading-relaxed text-warning">
          {t(
            'proposal.handoffDropped',
            "This page has no field for: {{keys}}. You'll need to add that yourself.",
            {
              keys: dropped.join(', '),
            },
          )}
        </p>
      )}
    </>
  );
}
